import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";

import { useUsers } from "@/api/queries";
import { isStaff, useCurrentUser } from "@/app/auth";
import { PageHeader } from "@/components/common/PageHeader";
import { NativeSelect } from "@/components/ui/input";
import { dateInZone } from "@/lib/format";
import { periodRange, type PresetPeriod } from "@/lib/periods";

import { ActivityTabs } from "./ActivityTabs";
import { AppsReportView } from "./AppsReportView";
import { TeamSelect } from "./TeamSelect";

const periods = ["today", "yesterday", "week", "lastWeek", "month", "custom"] as const;
type Period = (typeof periods)[number];

export function AppsPage() {
  const { t } = useTranslation();
  const me = useCurrentUser();
  const staff = isStaff(me);
  const [params, setParams] = useSearchParams();
  const today = dateInZone(new Date(), me.timezone);
  const period = (periods as readonly string[]).includes(params.get("period") ?? "")
    ? (params.get("period") as Period)
    : "week";
  const [from, to] =
    period === "custom"
      ? [params.get("from") ?? today, params.get("to") ?? today]
      : periodRange(period as PresetPeriod, today);
  const userId = (staff && params.get("user")) || undefined;
  const teamId = (staff && !userId && params.get("team")) || undefined;
  const users = useUsers();

  const set = (changes: Record<string, string | undefined>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(changes)) {
      if (v) p.set(k, v);
      else p.delete(k);
    }
    setParams(p, { replace: true });
  };

  return (
    <>
      <PageHeader
        title={t("nav.activity")}
        actions={
          staff && (
            <>
              <NativeSelect
                aria-label={t("scope.employee")}
                value={userId ?? ""}
                onChange={(e) => set({ user: e.target.value || undefined, team: undefined })}
              >
                <option value="">{t("scope.allPeople")}</option>
                {(users.data ?? [])
                  .filter((u) => u.status !== "invited")
                  .map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.full_name}
                    </option>
                  ))}
              </NativeSelect>
              {!userId && (
                <TeamSelect
                  value={teamId}
                  onChange={(team) => set({ team })}
                  allLabel={t("scope.allTeams")}
                />
              )}
            </>
          )
        }
      />
      <ActivityTabs />

      <AppsReportView
        scope={{ from, to, userIds: userId ? [userId] : undefined, teamId }}
        multiPeople={!userId && staff}
        toolbar={
          <>
            <NativeSelect
              aria-label={t("scope.period")}
              value={period}
              onChange={(e) => {
                const next = e.target.value as Period;
                set(
                  next === "custom"
                    ? { period: next, from, to }
                    : { period: next, from: undefined, to: undefined },
                );
              }}
            >
              {periods.map((p) => (
                <option key={p} value={p}>
                  {t(`apps.periods.${p}`)}
                </option>
              ))}
            </NativeSelect>
            {period === "custom" && (
              <>
                <input
                  type="date"
                  aria-label={t("apps.from")}
                  value={from}
                  max={today}
                  onChange={(e) => e.target.value && set({ from: e.target.value })}
                  className="h-9 rounded-lg border bg-surface px-3 font-medium shadow-card"
                />
                <span className="text-muted">—</span>
                <input
                  type="date"
                  aria-label={t("apps.to")}
                  value={to}
                  max={today}
                  onChange={(e) => e.target.value && set({ to: e.target.value })}
                  className="h-9 rounded-lg border bg-surface px-3 font-medium shadow-card"
                />
              </>
            )}
          </>
        }
      />
    </>
  );
}
